import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { getHowILearn } from "@/lib/how-i-learn.functions";
import { getMyWeeklySummary } from "@/lib/weekly-summary.functions";
import { Sparkles, Target, TrendingUp, Star, Layers } from "lucide-react";

export const Route = createFileRoute("/learner/how-i-learn")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "How I Learn — MAKAI" },
      {
        name: "description",
        content:
          "A transparent view of what MAKAI has observed about your learning — strengths, focus skills, recent improvement, and helpful formats.",
      },
      { property: "og:title", content: "How I Learn — MAKAI" },
      {
        property: "og:description",
        content:
          "What MAKAI has learned about your learning: strengths, focus skills, and what's helping you improve.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: HowILearnPage,
});

function HowILearnPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["how-i-learn"],
    queryFn: () => getHowILearn(),
  });
  const { data: week } = useQuery({
    queryKey: ["how-i-learn", "weekly"],
    queryFn: () => getMyWeeklySummary(),
  });

  return (
    <AppShell
      role="learner"
      title="How I learn"
      subtitle="What MAKAI has observed about your learning."
    >
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : !data || data.statements.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-6">
          {/* MAKAI HAS LEARNED */}
          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" />
              </div>
              <h2 className="font-display text-lg font-bold">MAKAI has learned</h2>
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {data.statements.map((s, i) => (
                <li
                  key={i}
                  className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2"
                >
                  {s}
                </li>
              ))}
            </ul>
          </section>

          {/* Current focus */}
          {data.current_focus && (
            <section className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
              <div className="flex items-center gap-2 text-primary">
                <Target className="h-4 w-4" />
                <div className="text-xs font-semibold uppercase tracking-wide">
                  Current focus
                </div>
              </div>
              <div className="mt-1 font-display text-xl font-bold">
                {data.current_focus.node_label}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Why? </span>
                {data.current_focus.why}
              </div>
            </section>
          )}

          {/* Weekly summary */}
          {week && week.bullets.length > 0 && (
            <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <h3 className="font-display text-base font-bold">{week.headline}</h3>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {week.bullets.map((b, i) => (
                  <li key={i}>• {b}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Two-column detail */}
          <div className="grid gap-4 md:grid-cols-2">
            <DetailCard
              icon={Star}
              title="My strengths"
              items={data.strengths.map((s) => ({
                title: s.node_label,
                subtitle: s.subject ?? undefined,
                note: s.note,
              }))}
              empty="MAKAI is still learning your strengths."
            />
            <DetailCard
              icon={Target}
              title="My focus skills"
              items={data.focus_skills.map((s) => ({
                title: s.node_label,
                subtitle: s.subject ?? undefined,
                note: s.note,
              }))}
              empty="No focus skills right now — keep practising."
            />
            <DetailCard
              icon={TrendingUp}
              title="My recent improvement"
              items={data.recent_improvement.map((s) => ({
                title: s.node_label,
                note: s.note,
              }))}
              empty="Complete a few more activities to see improvement here."
            />
            <DetailCard
              icon={Layers}
              title="My learning style"
              items={data.learning_style.map((s) => ({
                title: s.format,
                subtitle: s.node_label,
                note: s.note,
              }))}
              empty="Try different formats — MAKAI will notice what helps most."
            />
          </div>

          <p className="text-xs text-muted-foreground">
            MAKAI describes what it observes in your practice. It does not label
            you or make claims about who you are.
          </p>
        </div>
      )}
    </AppShell>
  );
}

function DetailCard({
  icon: Icon,
  title,
  items,
  empty,
}: {
  icon: typeof Star;
  title: string;
  items: Array<{ title: string; subtitle?: string; note: string }>;
  empty: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-muted text-foreground">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-display text-base font-bold">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-3 space-y-3 text-sm">
          {items.map((it, i) => (
            <li key={i}>
              <div className="font-medium">{it.title}</div>
              {it.subtitle && (
                <div className="text-xs text-muted-foreground">{it.subtitle}</div>
              )}
              <div className="text-muted-foreground">{it.note}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
      <Sparkles className="mx-auto h-8 w-8 text-primary" />
      <h2 className="mt-3 font-display text-lg font-bold">
        MAKAI is still learning about your learning
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Complete a few lessons, quizzes, or practice questions and this page
        will show what MAKAI has observed — your strengths, focus skills, and
        what's helping you improve.
      </p>
    </div>
  );
}
